import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRealtime } from "@/lib/realtime";
import DashboardLayout from "@/components/DashboardLayout";
import WelcomeModal from "@/components/WelcomeModal";
import RankCongratsBanner from "@/components/RankCongratsBanner";
import { User, Copy, ShoppingBag, ChevronRight, Phone, Camera, Clock, CheckCircle2, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

/**
 * SmartDashboard — SmartValue-inspired 3-column dashboard for IBAs.
 * Every value comes from a REAL API — nothing hardcoded. Fields that don't
 * have a source yet render as "—" so the samara-plan structure is visible
 * but the reader knows the number will fill in as business grows.
 *
 * APIs consumed:
 *   • GET /dist/overview     — profile, income, weekly volumes, recent orders
 *   • GET /rewards/me        — current rank, next rank, annual pairs, tier progress
 *   • GET /public/settings   — helpline + download PDFs (admin-configurable)
 */

/**
 * Returns the current Samara business week number.
 *
 * Priority:
 *   1. If admin has set `fy_start_date` in system_settings, count weeks from
 *      there — this is Samara's actual go-live date.
 *   2. Otherwise fall back to the ISO-8601 week number of the current year
 *      (1-53) so the value is always a real, meaningful number and never a
 *      fake "127" copied from a competitor's fiscal calendar.
 */
function samaraWeek(fyStartDate) {
  if (fyStartDate) {
    const start = new Date(fyStartDate);
    if (!isNaN(start.getTime())) {
      const weeks = Math.floor((new Date() - start) / (7 * 86400000)) + 1;
      return weeks > 0 ? weeks : 1;
    }
  }
  // ISO week-of-year fallback (Thursday-anchored, matches banking/HR systems).
  const d = new Date();
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((target - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

const fmtN = (n) => (n === null || n === undefined
  ? "—"
  : new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n));

const orBlank = (v) => (v === null || v === undefined || v === "" ? "—" : v);

export default function SmartDashboard() {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const [d, setD] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [settings, setSettings] = useState(null);
  const week = useMemo(() => samaraWeek(settings?.fy_start_date), [settings?.fy_start_date]);

  const refetch = useCallback(() => {
    // Rule R-10 (2026-10-05): Dashboard calls -frozen variants so SP
    // additions after the last paid closing are invisible here. Account
    // Info and Wallet pages continue to use the live /dist/overview and
    // /rewards/me endpoints.
    api.get("/dist/overview-frozen").then((r) => setD(r.data)).catch(() => {});
    api.get("/rewards/me-frozen").then((r) => setRewards(r.data)).catch(() => setRewards({}));
    api.get("/public/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);
  useEffect(() => { refetch(); }, [refetch]);

  useEffect(() => {
    let t = null;
    const deb = () => { if (t) clearTimeout(t); t = setTimeout(() => { refetch(); t = null; }, 500); };
    const offs = [
      subscribe("wallet.update", deb),
      subscribe("bv.update", deb),
      subscribe("order.created", deb),
      subscribe("package.activated", deb),
      subscribe("closing.completed", deb),
    ];
    return () => { offs.forEach((o) => o()); if (t) clearTimeout(t); };
  }, [subscribe, refetch]);

  if (!d) {
    return (
      <DashboardLayout title="My Dashboard">
        <div className="text-navy/70 text-sm font-semibold">Loading…</div>
      </DashboardLayout>
    );
  }

  const p = d.profile;
  const inc = d.income || {};
  const vol = d.generation?.volume || {};
  const acc = vol.accumulated || {};
  const monthly = vol.current_month || {};
  const prevM = vol.till_last_month || {};

  const leftBv = user?.left_sp || 0;
  const rightBv = user?.right_sp || 0;
  const totalLR = leftBv + rightBv || 1;

  const refLink = `${window.location.origin}/register?sponsor=${p.user_code}`;
  const qrIba = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(refLink)}&color=0F172A&bgcolor=FFFFFF&margin=0`;

  // v37 rev 8.5l (2026-10-03 night) — Dashboard dedupe. The former
  // "Sales Point Business Summary" card duplicated LastClosingPaidCard
  // row-for-row. Removed per operator request ("hide duplicates, give
  // clean picture"). LastClosingPaidCard is now the single source of
  // truth for the frozen committed-row view.

  const downloads = settings?.downloads || [];
  const helpline = (settings?.helpline_number || "").trim();
  const helplineHours = (settings?.helpline_hours || "").trim();

  return (
    <DashboardLayout
      title="My Dashboard"
      subtitle={p.user_code}
      subtitleClassName="mt-0.5 font-mono font-extrabold tracking-wider text-navy/80 text-sm md:text-base"
      actions={<WeeklyCapChip weekly={rewards?.weekly_cap} />}
    >
      <WelcomeModal />
      <RankCongratsBanner />
      <DashboardAsOfStrip d={d} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="smart-dashboard">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-3 space-y-4">
          <QrCard title="Scan QR for IBA Registration" img={qrIba} caption={p.user_code} link={refLink} />
          <MyProfile p={p} photoUrl={user?.profile_photo_url} />
          <NewsCard notices={d.notices} />
          <CreditBalanceCard wallets={inc.wallets || {}} walletDisplay={d.wallet_display} serverTimeIst={d.closing_state?.ist_server_time} />
        </div>

        {/* MIDDLE COLUMN */}
        <div className="lg:col-span-5 space-y-4">
          <SamaraAnimatedLogo />
          {helpline ? <HelplineBanner number={helpline} hours={helplineHours} /> : null}
          {d.last_closing_paid ? <LastClosingPaidCard row={d.last_closing_paid} /> : <NoClosingCard />}
          {/* v37 rev 8.5m (2026-10-03 night) — operator-correction (Javed):
              ClosingPendingCard was removed entirely. It duplicated
              LastClosingPaidCard on paid rows and the "weekly closing
              pending" banner is covered by the ProjectedPayoutCard /
              upcoming projection. Server-time now lives in the
              CreditBalanceCard footer. */}
          {d.closing_state?.show_projection !== false
            ? <ProjectedPayoutCard payout={rewards?.projected_weekly_payout} weekNo={week} />
            : null}
        </div>

        {/* RIGHT COLUMN */}
        <div className="lg:col-span-4 space-y-2.5">
          <DownloadList items={downloads} />
          <RecognitionRetail acc={acc} weekNo={week} />
          <RecognitionSalesPoint rewards={rewards} weekNo={week} active={!!(p.active && p.activated_at)} />
          <RecognitionMonthlyBonus rewards={rewards} />
          <RecentOrders orders={d.recent_orders || []} />
        </div>
      </div>
    </DashboardLayout>
  );
}

/* ---------- Sub-components ---------- */

const fmtPeriod = (label) => {
  const m = String(label || "").match(/^(\d{2})_([a-z]{3})_to_(\d{2})_([a-z]{3})_(\d{4})$/i);
  if (!m) return String(label || "").replace(/_/g, " ");
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  return `${m[1]} ${cap(m[2])} → ${m[3]} ${cap(m[4])} ${m[5]}`;
};

function DashboardAsOfStrip({ d }) {
  // R-10 v2: every figure on this page is as of the last completed closing.
  if (!d?._dashboard_frozen_period) return null;
  const at = d._dashboard_frozen_cutoff ? new Date(d._dashboard_frozen_cutoff) : null;
  const atFmt = at && !isNaN(at.getTime())
    ? `${at.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })} IST`
    : "";
  const next = d.closing_state?.pending_label;
  return (
    <div data-testid="dashboard-as-of-strip" className="mb-4 bg-white border border-sky-200 rounded-lg px-4 py-2.5 flex flex-col md:flex-row md:items-center md:justify-between gap-1.5 text-xs shadow-sm">
      <div className="text-navy">
        <span className="font-bold uppercase tracking-wider text-sky-700">Figures as of last completed closing:</span>{" "}
        <span className="font-extrabold" data-testid="dashboard-as-of-period">{fmtPeriod(d._dashboard_frozen_period)}</span>
        {atFmt ? <span className="text-navy/60" data-testid="dashboard-as-of-closed-at"> · closed {atFmt}</span> : null}
      </div>
      <div className="text-navy/70">
        {next ? <>Next closing: <span className="font-bold text-navy" data-testid="dashboard-next-closing">{fmtPeriod(next)}</span> · </> : null}
        SP added after this closing:{" "}
        <Link to="/account-info" className="font-bold text-sky-700 underline hover:text-sky-900 transition-colors" data-testid="dashboard-as-of-account-info-link">see Account Info</Link>
      </div>
    </div>
  );
}

function WeeklyCapChip({ weekly }) {
  // Shows how much of the ₹2,50,000 weekly Pair-Matching cap has been used
  // this ISO week + how much SP will carry into next week. Renders "—" when
  // the /rewards/me payload hasn't arrived yet so the header stays stable.
  if (!weekly) {
    return (
      <span
        data-testid="weekly-cap-chip"
        className="hidden sm:inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-navy/15 bg-white text-navy/40"
        title="Weekly Pair-Matching cap tracker"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-navy/30" /> This week — / —
      </span>
    );
  }
  const paid = Number(weekly.paid_rs || 0);
  const cap  = Number(weekly.cap_rs  || 0);
  const pct  = Number(weekly.pct_used || 0);
  const carry = Number(weekly.carry_left_sp || 0) + Number(weekly.carry_right_sp || 0);
  const nearCap = pct >= 80;
  const atCap   = pct >= 100;
  const cls = atCap
    ? "border-rose/40 bg-rose/10 text-rose"
    : nearCap
      ? "border-amber-400/50 bg-amber-50 text-amber-800"
      : "border-olive/30 bg-olive/5 text-olive";
  const dot = atCap ? "bg-rose animate-pulse" : nearCap ? "bg-amber-500" : "bg-olive";
  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
  return (
    <span
      data-testid="weekly-cap-chip"
      title={`Weekly Pair-Matching cap · Period ${weekly.period}\nPaid: ${inr(paid)} of ${inr(cap)}\nCarry-forward this week: ${carry} SP total`}
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border font-semibold ${cls}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      <span className="tabular-nums">{inr(paid)} / {inr(cap)}</span>
      <span className="opacity-70">·</span>
      <span className="tabular-nums">{carry} SP carry</span>
    </span>
  );
}

function QrCard({ title, img, caption, link }) {
  const doCopy = () => {
    navigator.clipboard.writeText(link);
    toast.success("Referral link copied");
  };
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white text-center px-3 py-2.5 text-sm font-bold">{title}</div>
      <div className="p-4 flex flex-col items-center">
        <img src={img} alt={title} className="w-40 h-40 object-contain" />
        {caption ? <div className="mt-2 text-xs font-bold text-navy tabular-nums">{caption}</div> : null}
        {link ? (
          <button onClick={doCopy} data-testid="smart-copy-ref" className="mt-2 text-xs px-3 py-1.5 border border-slate-300 rounded-md text-navy font-semibold hover:bg-slate-50 inline-flex items-center gap-1">
            <Copy size={11} /> Copy link
          </button>
        ) : null}
      </div>
    </div>
  );
}

function MyProfile({ p, photoUrl }) {
  const row = (label, value) => (
    <div className="flex items-start gap-2 border-b border-slate-100 last:border-0 py-1.5">
      <div className="text-xs font-bold uppercase tracking-wider text-navy/70 w-24 shrink-0">{label}</div>
      <div className="text-sm font-semibold text-navy break-words min-w-0 flex-1">{orBlank(value)}</div>
    </div>
  );
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid="smart-profile">
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 text-sm font-bold">My Profile</div>
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-100">
          <Link to="/profile" className="w-16 h-16 rounded-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 grid place-items-center border-2 border-slate-300 shrink-0 hover:border-sky-500 transition-colors relative group" data-testid="smart-profile-avatar-link" title="Upload / change photo">
            {photoUrl ? (
              <img src={photoUrl} alt={p.name} className="w-full h-full object-cover" data-testid="smart-profile-photo" />
            ) : (
              <User size={32} className="text-slate-500" />
            )}
            <span className="absolute inset-x-0 bottom-0 bg-sky-600/90 text-white text-[9px] font-bold text-center py-0.5 opacity-0 group-hover:opacity-100 transition-opacity">Change</span>
          </Link>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-lg font-extrabold text-navy truncate">{p.name}</div>
            <div className="text-xs uppercase tracking-wider text-navy/70 font-bold">{p.user_code}</div>
            <Link to="/profile" className="inline-flex items-center gap-1 mt-1.5 text-xs px-2 py-1 bg-sky-500 text-white font-bold rounded-md hover:bg-sky-600 transition-colors" data-testid="smart-profile-edit">
              <Camera size={11} /> {photoUrl ? "Change photo" : "Upload photo"}
            </Link>
          </div>
        </div>
        {row("Samara ID", p.user_code)}
        {row("Name", p.name)}
        {row("Reg. Date", p.doj)}
        {row("Mobile", p.mobile)}
        {row("Email", p.email)}
        {row("State", p.state)}
        {row("District", p.district)}
        {row("City", p.city)}
        {row("Address", p.full_address)}
        {row("Pincode", p.pin_code)}
      </div>
    </div>
  );
}

function NewsCard({ notices }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 text-sm font-bold">News</div>
      <div className="p-4">
        {notices && notices.length > 0 ? (
          <ul className="space-y-2 text-sm text-navy">
            {notices.slice(0, 3).map((n, i) => (
              <li key={i} className="border-l-4 border-sky-500 pl-2.5">
                <div className="font-bold">{n.title}</div>
                <div className="text-navy/70 text-xs mt-0.5 font-medium">{n.body}</div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-navy/60 font-medium">—</div>
        )}
      </div>
    </div>
  );
}

function CreditBalanceCard({ wallets, walletDisplay, serverTimeIst }) {
  // v37 rev 8.5m (2026-10-03 night) — Javed's operator corrections:
  //   • "Payment Pending" tile removed (operator rule: closing pending IS
  //     the only pending; projection is NOT a wallet state).
  //   • "Pending Payouts" sub-header removed (not needed).
  //   • 3 flat tiles only: Closing Pending · Settlements Paid (Lifetime)
  //     · Shopping Wallet · ACB.
  //   • Server time surfaced in the footer so the removed ClosingPendingCard's
  //     timestamp is preserved on this earlier card.
  const wd = walletDisplay || {};
  const inrFmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
  const asOf = wd.as_of_utc
    ? new Date(wd.as_of_utc).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) + " IST"
    : "—";

  const closingPending     = Number(wd.closed_payment_pending || 0);
  const closingPendingRows = wd.closed_payment_pending_rows || [];
  const paidHistory        = Number(wd.paid_history_total    || 0);
  const paidHistoryRows    = Number(wd.paid_history_rows     || 0);
  const noClosing          = paidHistoryRows === 0 && closingPendingRows.length === 0;
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid="smart-credit-balance">
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 text-sm font-bold flex items-center justify-between">
        <span>Wallet Summary</span>
        <span className="text-[10px] font-normal text-white/80" data-testid="wallet-as-of">as of {asOf}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
        <div
          className={`px-3 py-2.5 border-b md:border-b-0 md:border-r border-slate-200 ${closingPending > 0 ? "bg-amber-50/70" : ""}`}
          data-testid="wallet-closing-pending"
          title={closingPendingRows.map((r) => `${r.period_label}: ${inrFmt(r.net_rs)}`).join("\n") || undefined}
        >
          <div className="text-[9px] uppercase tracking-widest text-amber-800 font-bold mb-0.5 inline-flex items-center gap-1">
            <Clock size={10} /> Closing Pending
          </div>
          <div className={`text-base font-extrabold tabular-nums ${closingPending > 0 ? "text-amber-800" : "text-navy/70"}`} data-testid="wallet-closing-pending-value">
            {noClosing ? "No closing recorded" : inrFmt(closingPending)}
          </div>
          <div className="text-[9px] text-navy/50 mt-0.5">
            {closingPending > 0
              ? `Closing finalized · Payment pending (${closingPendingRows.length})`
              : noClosing ? "first closing not finalized yet" : "no committed closings pending"}
          </div>
        </div>
        <div className="px-3 py-2.5 border-b md:border-b-0 md:border-r border-slate-200" data-testid="wallet-paid-history">
          <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold mb-0.5 inline-flex items-center gap-1">
            <CheckCircle2 size={10} /> Settlements Paid (Lifetime)
          </div>
          <div className="text-base font-extrabold text-navy tabular-nums" data-testid="wallet-paid-history-value">{noClosing ? "No closing recorded" : inrFmt(paidHistory)}</div>
          <div className="text-[9px] text-navy/50 mt-0.5">
            {noClosing ? "nothing disbursed yet" : `${paidHistoryRows} closing${paidHistoryRows === 1 ? "" : "s"} · disbursed`}
          </div>
        </div>
        <div className="px-3 py-2.5" data-testid="wallet-shopping">
          <div className="text-[9px] uppercase tracking-widest text-sky-700 font-bold mb-0.5">Shopping Wallet · ACB</div>
          <div className="text-base font-extrabold text-navy tabular-nums">{fmtN(wallets?.shopping)}</div>
          <div className="text-[9px] text-navy/50 mt-0.5">product-credit balance</div>
        </div>
      </div>
      <div className="px-3 py-1.5 border-t border-slate-200 text-[10px] text-navy/50 bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
        <span>Closing Pending clears as finance disburses</span>
        {serverTimeIst ? (
          <span className="font-mono" data-testid="wallet-server-time">
            Server time: {serverTimeIst}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function HelplineBanner({ number, hours }) {
  return (
    <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white rounded-lg px-4 py-2.5 text-sm font-bold shadow-sm flex items-center gap-2" data-testid="helpline-banner">
      <Phone size={14} /> Helpline: {number}{hours ? ` (${hours})` : ""}
    </div>
  );
}

function SamaraAnimatedLogo() {
  return (
    <div className="flex justify-center items-center py-2" data-testid="samara-animated-logo">
      <img
        src="https://customer-assets-agu9un31.emergentagent.net/job_wellness-direct-sell/artifacts/snfi9hyl_Samara_Logo_Animated_Low_Size.gif"
        alt="Samara — Health · Education · Beauty"
        className="max-h-40 w-auto object-contain drop-shadow-md animate-logo-bob motion-reduce:animate-none"
        loading="lazy"
      />
    </div>
  );
}

function LastClosingPaidCard({ row }) {
  // Shown after `commit-weekly-closing`. The ₹ stays in `wallets.income` as
  // "unpaid payable" until finance disburses (Monday). Card status flips
  // from "Pending — Payable Monday" to "Paid" via mark-closing-paid.
  const p = row || {};
  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
  const label = String(p.period_label || "").replace(/_/g, " ").replace(/\bto\b/, "→");
  const closedAt = p.closed_at ? new Date(p.closed_at) : null;
  const closedFmt = closedAt && !isNaN(closedAt.getTime())
    ? closedAt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "";
  const paidAt = p.paid_at ? new Date(p.paid_at) : null;
  const paidFmt = paidAt && !isNaN(paidAt.getTime())
    ? paidAt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "";
  const isPaid = p.status === "paid";
  const isZero = Number(p.gross_rs || 0) === 0;
  const canDownload = isPaid && !isZero && !!p.period_label;
  // v37 rev 8.5m — Payout Confirmation PDF download.
  const [downloading, setDownloading] = useState(false);
  const downloadReceipt = useCallback(async () => {
    if (!p.period_label) return;
    try {
      setDownloading(true);
      // Use the preconfigured axios instance from @/lib/api so the auth
      // header is attached automatically. responseType=blob so the PDF
      // bytes aren't JSON-parsed.
      const resp = await api.get(
        `/dist/closings/${encodeURIComponent(p.period_label)}/payout-pdf`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `samara-payout-${p.period_label}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(href), 2000);
    } catch (e) {
      console.error("payout pdf download failed", e);
      alert("Could not download receipt. Please try again.");
    } finally {
      setDownloading(false);
    }
  }, [p.period_label]);
  const bandCls = isPaid
    ? (isZero ? "from-slate-500 to-slate-600" : "from-emerald-600 to-emerald-700")
    : "from-amber-500 to-amber-600";
  const netCls  = isPaid
    ? (isZero ? "bg-slate-50 text-slate-700" : "bg-emerald-50 text-emerald-800")
    : "bg-amber-50 text-amber-800";
  const netLabel = isPaid ? (isZero ? "Net" : "Net Paid") : "Net Payable";
  const grossLabel = isPaid ? (isZero ? "Gross" : "Gross Paid") : "Gross Booked";
  const statusChip = isPaid
    ? (isZero ? "— Zero Payout" : `✓ Paid · ${paidFmt || ""}`)
    : "⏱ Payment pending";
  const headerTitle = isPaid
    ? (isZero ? "Last Closing · Zero Payout" : "Last Closing Paid")
    : "Closing finalized · Payment pending";
  const footer = isPaid
    ? (isZero ? "Booked to weekly ledger · no earnings this period"
              : "Booked to weekly ledger · disbursement confirmed")
    : "Closing finalized · marked paid after the bank transfer";
  return (
    <div
      className={`bg-white border ${isPaid ? "border-emerald-500/40" : "border-amber-400/50"} rounded-lg overflow-hidden shadow-sm`}
      data-testid="last-closing-paid-card"
    >
      <div className={`bg-gradient-to-r ${bandCls} text-white px-4 py-2.5 flex items-center justify-between gap-2`}>
        <div className="flex items-center gap-2">
          <span className="text-lg">{isPaid ? (isZero ? "—" : "✓") : "⏱"}</span>
          <div className="text-sm font-bold uppercase tracking-wide">
            {headerTitle}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canDownload ? (
            <button
              type="button"
              onClick={downloadReceipt}
              disabled={downloading}
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded border border-white/40 bg-white/15 hover:bg-white/25 disabled:opacity-50 transition-colors"
              data-testid="lcp-download-pdf"
              title="Download your payout confirmation PDF"
            >
              <Download size={11} /> {downloading ? "Preparing…" : "Receipt"}
            </button>
          ) : null}
          <div className="text-[10px] font-bold bg-white/25 px-2 py-0.5 rounded whitespace-nowrap" data-testid="lcp-status">
            {statusChip}
          </div>
        </div>
      </div>
      <div className={`px-4 py-2 ${isPaid ? "bg-emerald-50/60 border-emerald-200/60" : "bg-amber-50/70 border-amber-200/60"} border-b`}>
        <div className={`text-[10px] uppercase tracking-[0.24em] ${isPaid ? "text-emerald-700" : "text-amber-800"} font-bold`} data-testid="lcp-period">
          Period: {label || "—"}{closedFmt ? ` · closed ${closedFmt}` : ""}
        </div>
      </div>
      <table className="w-full text-sm">
        <tbody>
          {p.pair_income_rs > 0 && (
            <tr className="border-b border-slate-100">
              <td className="px-3 py-1.5 text-navy/80 font-medium">Pair Matching (1:1) · {p.matched_sp} SP</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-navy font-semibold" data-testid="lcp-pair">{inr(p.pair_income_rs)}</td>
            </tr>
          )}
          {p.direct_ref_rs > 0 && (
            <tr className="border-b border-slate-100">
              <td className="px-3 py-1.5 text-navy/80 font-medium">Direct Referral 10%</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-navy font-semibold" data-testid="lcp-dref">{inr(p.direct_ref_rs)}</td>
            </tr>
          )}
          {p.monthly_bonus_rs > 0 && (
            <tr className="border-b border-slate-100">
              <td className="px-3 py-1.5 text-navy/80 font-medium">Monthly Bonus</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-navy font-semibold" data-testid="lcp-bonus">{inr(p.monthly_bonus_rs)}</td>
            </tr>
          )}
          {p.rank_reward_rs > 0 && (
            <tr className="border-b border-slate-100">
              <td className="px-3 py-1.5 text-navy/80 font-medium">Rank Reward</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-navy font-semibold" data-testid="lcp-rank">{inr(p.rank_reward_rs)}</td>
            </tr>
          )}
          <tr className="bg-navy">
            <td className="px-3 py-2 text-gold font-extrabold uppercase tracking-wide text-xs">{grossLabel}</td>
            <td className="px-3 py-2 text-right tabular-nums text-gold font-extrabold text-base" data-testid="lcp-gross">{inr(p.gross_rs)}</td>
          </tr>
          {Number(p.withdrawn_deduction_rs || 0) > 0 && (
            <tr className="border-b border-slate-100">
              <td className="px-3 py-1.5 text-navy/70 text-xs">Less: already withdrawn</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-rose-700 font-semibold text-xs" data-testid="lcp-withdrawn">− {inr(p.withdrawn_deduction_rs)}</td>
            </tr>
          )}
          <tr className="border-b border-slate-100">
            <td className="px-3 py-1.5 text-navy/70 text-xs">TDS ({Number(p.tds_rate_pct || 0).toFixed(0)}%)</td>
            <td className="px-3 py-1.5 text-right tabular-nums text-rose-700 font-semibold text-xs" data-testid="lcp-tds">− {inr(p.tds_amount_rs)}</td>
          </tr>
          <tr className={netCls}>
            <td className="px-3 py-2 font-extrabold uppercase tracking-wide text-xs">{netLabel}</td>
            <td className="px-3 py-2 text-right tabular-nums font-extrabold text-base" data-testid="lcp-net">{inr(p.net_rs)}</td>
          </tr>
          {/* Carry-forward SP going into next closing */}
          <tr className="bg-slate-50">
            <td className="px-3 pt-2 pb-0.5 text-navy/60 uppercase tracking-widest text-[10px] font-bold" colSpan={2}>Carry-forward into next closing</td>
          </tr>
          <tr className="bg-slate-50 border-b border-slate-100">
            <td className="px-3 py-1 text-navy/80 text-xs font-medium">Pair Carry Forward L / R</td>
            <td className="px-3 py-1 text-right tabular-nums text-navy font-semibold text-sm" data-testid="lcp-pair-carry">
              <span className="text-emerald-700">{Number(p.pair_carry_left_sp || 0)}</span>
              <span className="text-navy/40 mx-1">/</span>
              <span className="text-emerald-700">{Number(p.pair_carry_right_sp || 0)}</span>
              <span className="text-navy/40 ml-1 text-xs">SP</span>
            </td>
          </tr>
          <tr className="bg-slate-50">
            <td className="px-3 py-1 text-navy/80 text-xs font-medium">Bonus Carry Forward L / R</td>
            <td className="px-3 py-1 text-right tabular-nums text-navy font-semibold text-sm" data-testid="lcp-bonus-carry">
              <span className="text-emerald-700">{Number(p.bonus_carry_left_sp || 0)}</span>
              <span className="text-navy/40 mx-1">/</span>
              <span className="text-emerald-700">{Number(p.bonus_carry_right_sp || 0)}</span>
              <span className="text-navy/40 ml-1 text-xs">SP</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div className="px-3 py-1.5 bg-slate-50 border-t border-slate-200 text-[10px] text-navy/60 italic">
        {footer}
      </div>
    </div>
  );
}

function NoClosingCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid="no-closing-card">
      <div className="bg-gradient-to-r from-slate-500 to-slate-600 text-white px-4 py-2.5 text-sm font-bold uppercase tracking-wide">
        Last Closing
      </div>
      <div className="px-4 py-4">
        <div className="text-base font-extrabold text-navy" data-testid="no-closing-text">No closing recorded</div>
        <div className="text-xs text-navy/70 mt-1">Your figures appear here once your first weekly closing is finalized.</div>
      </div>
    </div>
  );
}

function ProjectedPayoutCard({ payout, weekNo }) {
  // customer rule (18 Feb 2026):
  //   1. Weekly Payout  = Pair Matching (1:1) + Direct Referral 10%
  //   2. Bonus          = Monthly Bonus + Rank Reward
  // Gross = section 1 + section 2. TDS 2% (assume PAN); 20% only when
  // pan_status='missing'. Carry-forward is shown per-leg (Left / Right)
  // so IBAs see exactly how many SP roll into next week.
  const p = payout || {};
  const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
  const gross = Number(p.gross_rs || 0);
  const net = Number(p.net_rs || 0);
  const weeklySub = Number(p.weekly_subtotal_rs ?? (Number(p.pair_income_rs || 0) + Number(p.direct_ref_rs || 0)));
  const bonusSub = Number(p.bonus_subtotal_rs ?? (Number(p.monthly_bonus_rs || 0) + Number(p.rank_reward_rs || 0)));
  const hasEarnings = gross > 0;
  const carryL = Number(p.carry_left_sp ?? 0);
  const carryR = Number(p.carry_right_sp ?? 0);
  const matched = Number(p.matched_sp ?? 0);
  return (
    <div
      className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm"
      data-testid="projected-payout-card"
    >
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 flex items-center justify-between gap-2">
        <div className="text-sm font-bold">This Week's Projected Payout</div>
        <div className="text-xs font-bold bg-white/25 px-2 py-0.5 rounded whitespace-nowrap">
          Week No: {weekNo}
        </div>
      </div>
      {!hasEarnings ? (
        <div className="p-4 text-xs text-navy/60 font-medium">
          Nothing pending for this closing. Build pair-matching volume, add direct referrals,
          or cross a Monthly Bonus level to see a projection here.
        </div>
      ) : (
        <>
          {/* Section 1 · Weekly Payout (regular income) */}
          <div className="px-3 pt-2.5 pb-1.5 bg-slate-50 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.28em] text-sky-700 font-bold">Weekly Payout · Regular Income</div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-2 text-navy font-semibold">Pair/Matching Income (1:1)</td>
                <td className="px-3 py-2 text-right tabular-nums text-navy font-medium" data-testid="projected-pair">
                  {inr(p.pair_income_rs)}
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-2 text-navy font-semibold">Direct Referral Income (10%)</td>
                <td className="px-3 py-2 text-right tabular-nums text-navy font-medium" data-testid="projected-directref">
                  {inr(p.direct_ref_rs)}
                </td>
              </tr>
              <tr className="bg-sky-50/60 border-b border-slate-200">
                <td className="px-3 py-1.5 text-navy/70 font-bold uppercase tracking-wide text-[10px]">Weekly Payout Subtotal</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-navy font-bold text-sm" data-testid="projected-weekly-subtotal">
                  {inr(weeklySub)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Section 2 · Bonus (shown separately per customer rule) */}
          <div className="px-3 pt-2.5 pb-1.5 bg-amber-50/80 border-b border-amber-200/60">
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-700 font-bold">Bonus · Separate</div>
          </div>
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-2 text-navy font-semibold">
                  Monthly Bonus
                  {p.monthly_bonus_tier && (
                    <span className="ml-2 text-[10px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded font-mono">
                      L{p.monthly_bonus_tier.tier} · {p.monthly_bonus_tier.high_sp}:{p.monthly_bonus_tier.match_sp}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-navy font-medium" data-testid="projected-monthly">
                  {inr(p.monthly_bonus_rs)}
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-2 text-navy font-semibold">Rank Reward</td>
                <td className="px-3 py-2 text-right tabular-nums text-navy font-medium" data-testid="projected-rank">
                  {inr(p.rank_reward_rs)}
                </td>
              </tr>
              <tr className="bg-amber-50 border-b border-slate-200">
                <td className="px-3 py-1.5 text-navy/70 font-bold uppercase tracking-wide text-[10px]">Bonus Subtotal</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-navy font-bold text-sm" data-testid="projected-bonus-subtotal">
                  {inr(bonusSub)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Section 3 · Total + TDS + Net */}
          <table className="w-full text-sm border-t-2 border-slate-300">
            <tbody>
              <tr className="bg-navy border-b border-slate-100">
                <td className="px-3 py-2.5 text-gold font-extrabold uppercase tracking-wide text-xs">Total Weekly Payout</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-gold font-extrabold text-base" data-testid="projected-gross">
                  {inr(gross)}
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="px-3 py-2 text-navy/70 font-medium">
                  TDS (Section 194H · {Number(p.tds_rate_pct || 0).toFixed(0)}%)
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-700 font-semibold" data-testid="projected-tds">
                  − {inr(p.tds_amount_rs)}
                </td>
              </tr>
              <tr className="bg-olive/5">
                <td className="px-3 py-2.5 text-olive font-extrabold uppercase tracking-wide text-xs">Net Payout</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-olive font-extrabold text-base" data-testid="projected-net">
                  {inr(net)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Section 4 · Carry-forward after this week's pair-match */}
          <div className="px-3 py-2.5 bg-slate-50 border-t border-slate-200 text-[11px] text-navy/70">
            <div className="uppercase tracking-[0.22em] text-navy/50 font-bold text-[9px] mb-1">
              Carry-Forward · After This Week's Match
            </div>
            <div className="flex gap-4">
              <div className="flex-1 bg-white border border-slate-200 rounded px-2 py-1.5" data-testid="carry-left">
                <div className="text-[9px] uppercase text-navy/50 tracking-wide">Left Leg</div>
                <div className="tabular-nums font-extrabold text-navy text-sm">{carryL.toLocaleString("en-IN")} SP</div>
              </div>
              <div className="flex-1 bg-white border border-slate-200 rounded px-2 py-1.5" data-testid="carry-right">
                <div className="text-[9px] uppercase text-navy/50 tracking-wide">Right Leg</div>
                <div className="tabular-nums font-extrabold text-navy text-sm">{carryR.toLocaleString("en-IN")} SP</div>
              </div>
              <div className="flex-1 bg-sky-50 border border-sky-200 rounded px-2 py-1.5">
                <div className="text-[9px] uppercase text-sky-700 tracking-wide">Matched This Week</div>
                <div className="tabular-nums font-extrabold text-sky-700 text-sm">{matched.toLocaleString("en-IN")} SP</div>
              </div>
            </div>
          </div>

          {/* Section 4b · Bonus carry-forward (applied AFTER weekly match) */}
          <div className="px-3 py-2.5 bg-amber-50/60 border-t border-amber-200/50 text-[11px] text-navy/70">
            <div className="uppercase tracking-[0.22em] text-amber-700/80 font-bold text-[9px] mb-1">
              Bonus Carry-Forward · After This Week's Match
              {p.monthly_bonus_tier && (
                <span className="ml-2 text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded font-mono text-[9px] normal-case tracking-normal">
                  L{p.monthly_bonus_tier.tier} qual: {p.monthly_bonus_tier.high_sp}:{p.monthly_bonus_tier.match_sp}
                </span>
              )}
            </div>
            <div className="flex gap-4">
              <div className="flex-1 bg-white border border-amber-200 rounded px-2 py-1.5" data-testid="bonus-carry-left">
                <div className="text-[9px] uppercase text-navy/50 tracking-wide">Monthly Left</div>
                <div className="tabular-nums font-extrabold text-navy text-sm">
                  {Number(p.bonus_carry_left_sp || 0).toLocaleString("en-IN")} SP
                </div>
                <div className="text-[9px] text-navy/50 mt-0.5">
                  was {Number(p.monthly_left_sp || 0).toLocaleString("en-IN")} · used {Number((p.monthly_left_sp || 0) - (p.bonus_carry_left_sp || 0)).toLocaleString("en-IN")}
                </div>
              </div>
              <div className="flex-1 bg-white border border-amber-200 rounded px-2 py-1.5" data-testid="bonus-carry-right">
                <div className="text-[9px] uppercase text-navy/50 tracking-wide">Monthly Right</div>
                <div className="tabular-nums font-extrabold text-navy text-sm">
                  {Number(p.bonus_carry_right_sp || 0).toLocaleString("en-IN")} SP
                </div>
                <div className="text-[9px] text-navy/50 mt-0.5">
                  was {Number(p.monthly_right_sp || 0).toLocaleString("en-IN")} · used {Number((p.monthly_right_sp || 0) - (p.bonus_carry_right_sp || 0)).toLocaleString("en-IN")}
                </div>
              </div>
              <div className="flex-1 bg-amber-100/70 border border-amber-300 rounded px-2 py-1.5">
                <div className="text-[9px] uppercase text-amber-800 tracking-wide">Bonus Paid</div>
                <div className="tabular-nums font-extrabold text-amber-800 text-sm">
                  {inr(p.monthly_bonus_rs)}
                </div>
              </div>
            </div>
          </div>
          <div className="px-3 py-1.5 text-[10px] text-navy/45 italic bg-slate-50 border-t border-slate-100">
            Live projection — final amount is locked at Friday's weekly closing. Bonus SP carry-forward is applied after pair-income calculation.
          </div>
        </>
      )}
    </div>
  );
}

// v37 rev 8.5l (2026-10-03 night): `BusinessSummary` function removed —
// it duplicated LastClosingPaidCard row-for-row. LastClosingPaidCard is
// the single source of truth for the frozen committed closing row.

function DownloadList({ items }) {
  if (!items.length) {
    // No PDFs configured yet — keep the samara-plan section visible but blank.
    return (
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
        <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 text-sm font-bold">Downloads</div>
        <div className="p-4 text-sm text-navy/60 font-medium">—</div>
      </div>
    );
  }
  return (
    <>
      {items.map((c, i) => (
        <a
          key={c.label + i}
          href={c.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`dl-card-${i}`}
          className={`w-full text-left px-4 py-3 rounded-md font-semibold text-white text-sm shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex items-center justify-between gap-2 ${
            i % 2 === 0
              ? "bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700"
              : "bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700"
          }`}
        >
          <span className="pr-2">Click to view (or) Download {c.label}</span>
          {c.badge ? (
            <span className="bg-rose-500 text-white text-[10px] px-1.5 py-0.5 rounded font-extrabold shrink-0">{c.badge}</span>
          ) : (
            <ChevronRight size={16} className="shrink-0" />
          )}
        </a>
      ))}
    </>
  );
}

function RecognitionRetail({ acc, weekNo }) {
  // Retail recognition is driven by group_rv accumulation. Until an admin
  // configures retail pin thresholds we render "—" for the pin level.
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 text-sm font-bold">Retail Recognition Level Details</div>
      <div className="p-4 space-y-1">
        <RowKV k="Rank" v="—" bold />
        <RowKV k={`Total steps earned till week ${weekNo}`} v={fmtN(acc.total_rv)} />
        <RowKV k="Cumulative Level & Steps" v={fmtN(acc.total_rv)} />
      </div>
    </div>
  );
}

function RecognitionSalesPoint({ rewards, weekNo, active }) {
  const current = rewards?.current_rank;
  const next = rewards?.progress?.next_rank;
  const pairs = rewards?.annual_pairs;
  // An IBA becomes "Executive" (rank #1) only after they've purchased a
  // package AND it's paid — i.e. `user.active === true`. Until that flips
  // true the current rank is blank and the ladder starts with Executive
  // as the next target. Once active, real rank/next-rank flow through
  // from /rewards/me exactly as the plan defines.
  const shownCurrent = active ? current?.label : null;
  const shownNext    = active ? next?.label   : "Executive";
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 text-sm font-bold">Sales Point Recognition Level Details</div>
      <div className="p-4 space-y-1">
        <RowKV k="Rank" v={shownCurrent || "—"} bold />
        <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
          <span className="text-xs font-bold uppercase tracking-wider text-navy/70">Next Rank</span>
          {shownNext ? (
            <span className="bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-extrabold px-3 py-0.5 rounded shadow-sm">
              {shownNext.toUpperCase()}
            </span>
          ) : (
            <span className="text-sm text-navy/60 font-semibold">—</span>
          )}
        </div>
        <RowKV k={`Total pairs earned till week ${weekNo}`} v={fmtN(pairs)} />
        <RowKV k="Cumulative Pairs" v={fmtN(rewards?.lifetime_pairs)} />
      </div>
    </div>
  );
}

function RowKV({ k, v, bold = false }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0">
      <div className="text-xs font-bold uppercase tracking-wider text-navy/70 flex-1">{k}</div>
      <div className={`text-sm text-navy tabular-nums ${bold ? "font-extrabold" : "font-semibold"}`}>{v}</div>
    </div>
  );
}

function RecognitionMonthlyBonus({ rewards }) {
  // Monthly bonus is a 2:1 SP-ratio tier (see MONTHLY_BONUS_TIERS in rewards.py).
  // v37 rev 8.5n (2026-10-04 night): "Current Level" now means "highest
  // tier ever paid to this IBA" (lifetime), not "tier the current-month
  // SP satisfies". Monthly Bonus is first-time only, so a user paid
  // Level 1 in Sept and earning fresh SP in Oct correctly shows
  // Current=Level 1 · Next=Level 2 (not Current=Level 2 as pre-fix did).
  const cur    = rewards?.monthly_bonus_tier;         // lifetime-paid tier (or null)
  const next   = rewards?.next_monthly_bonus_tier;     // first unpaid tier (or null)
  const qualifies = rewards?.monthly_bonus_tier_qualifies_now; // this-month qualification (or null)
  const isStale = Boolean(rewards?.monthly_bonus_is_stale_rollover);
  const leftSp  = rewards?.monthly_left_sp;
  const rightSp = rewards?.monthly_right_sp;
  const higherSp   = (leftSp || 0) >= (rightSp || 0) ? leftSp : rightSp;
  const matchingSp = (leftSp || 0) >= (rightSp || 0) ? rightSp : leftSp;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid="smart-monthly-bonus">
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 text-sm font-bold">Monthly Bonus (2 : 1 SP Ratio)</div>
      <div className="p-4 space-y-1">
        <div className="grid grid-cols-[1fr_auto] items-center gap-3 py-2 border-b border-slate-100">
          <span className="text-xs font-bold uppercase tracking-wider text-navy/80 leading-tight flex flex-col gap-0.5">
            <span>Current Level</span>
            <span className="text-[10px] font-normal normal-case text-navy/50 italic">(lifetime paid)</span>
          </span>
          {cur?.tier ? (
            <span className="inline-flex items-center justify-center min-w-[120px] bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs font-extrabold px-3 py-1 rounded shadow-sm tabular-nums" data-testid="smart-mb-current">
              LEVEL {cur.tier} · ₹{fmtN(cur.bonus)}
            </span>
          ) : (
            <span className="text-sm text-navy/60 font-semibold" data-testid="smart-mb-current">— not yet claimed</span>
          )}
        </div>
        <div className="grid grid-cols-[1fr_auto] items-center gap-3 py-2 border-b border-slate-100">
          <span className="text-xs font-bold uppercase tracking-wider text-navy/80 leading-tight">Next Level</span>
          {next?.tier ? (
            <span className="inline-flex items-center justify-center min-w-[120px] bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-extrabold px-3 py-1 rounded shadow-sm tabular-nums" data-testid="smart-mb-next">
              LEVEL {next.tier} · ₹{fmtN(next.bonus)}
            </span>
          ) : (
            <span className="text-sm text-navy/60 font-semibold" data-testid="smart-mb-next">— max tier reached</span>
          )}
        </div>
        <RowKV k="Higher-side SP this month" v={fmtN(higherSp)} />
        <RowKV k="Matching-side SP this month" v={fmtN(matchingSp)} />
        {next?.tier ? (
          <RowKV k={`SP needed for Level ${next.tier}`} v={`${fmtN(next.high_sp)} / ${fmtN(next.match_sp)}`} />
        ) : null}
        {qualifies?.tier && cur?.tier && qualifies.tier > cur.tier ? (
          <div className="mt-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-[10px] text-emerald-800 font-semibold" data-testid="smart-mb-qualifies-chip">
            ✦ This month's SP already qualifies for LEVEL {qualifies.tier} · ₹{fmtN(qualifies.bonus)} — will be paid at month-end.
          </div>
        ) : null}
        {isStale ? (
          <div className="mt-1 px-2 py-1 bg-sky-50 border border-sky-200 rounded text-[10px] text-sky-800 italic" data-testid="smart-mb-rollover-chip">
            New month started · Higher/Matching SP reset to 0. Earn fresh SP this month to progress toward Level {next?.tier || "—"}.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RecentOrders({ orders }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid="smart-recent-orders">
      <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white px-4 py-2.5 text-sm font-bold flex items-center gap-2">
        <ShoppingBag size={15} /> Shopping (Recent Two Orders)
      </div>
      {orders.length === 0 ? (
        <div className="p-4 text-sm text-navy/60 font-medium">—</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 font-bold text-xs uppercase tracking-wider text-navy/80">Order No</th>
              <th className="text-right px-3 py-2 font-bold text-xs uppercase tracking-wider text-navy/80">Order Amt (₹)</th>
              <th className="text-right px-3 py-2 font-bold text-xs uppercase tracking-wider text-navy/80">Order SP</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o, i) => (
              <tr key={i} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2 font-mono text-xs text-navy font-bold">{o.order_no}</td>
                <td className="px-3 py-2 text-right tabular-nums font-extrabold text-navy">{fmtN(o.amount)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-extrabold text-navy">{fmtN(o.sp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
